import { Modal, Spin, Typography } from 'antd'
import dayjs from 'dayjs'
import { useContext } from 'react'
import { AuthenticationContext } from '../../../AuthenticationContext'
import useFeatureFlag from '../../../hooks/useFeatureFlag'
import MessageBubble from '../../AiAssistant/MessageBubble'
import { COMPACT_TOOL_CALLS_FEATURE } from '../../AiAssistant/messageBubbleModel'
import { useChatSession } from './chatSearchQueries'
import { convertSessionMessages } from './chatSearchModel'
import GoldenPromptControl from './GoldenPromptControl'
import {
  useCreateGoldenPromptMutation,
  useDeleteGoldenPromptMutation,
  useGoldenPromptsQuery,
} from './goldenPromptsQueries'
import {
  findGoldenPromptForUserMessage,
  getUserPromptText,
  isGoldenPromptPending,
} from './goldenPromptsModel'
import './ChatSessionModal.css'

const { Text } = Typography

type Props = {
  sessionId: string | null
  onClose: () => void
}

const ChatSessionModal = ({ sessionId, onClose }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const compactToolCalls = useFeatureFlag(COMPACT_TOOL_CALLS_FEATURE)
  const adminUserId = currentUser?.userId
  const { data, isLoading } = useChatSession(sessionId)
  const { data: goldenPrompts } = useGoldenPromptsQuery(adminUserId)
  const createGolden = useCreateGoldenPromptMutation(adminUserId)
  const deleteGolden = useDeleteGoldenPromptMutation(adminUserId)
  const messages = data ? convertSessionMessages(data.messages, compactToolCalls) : []

  return (
    <Modal
      open={!!sessionId}
      onCancel={onClose}
      footer={null}
      title={data ? `Chat — ${data.user_name}` : 'Chat Session'}
      width={720}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
    >
      {isLoading && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      )}
      {data && (
        <>
          <div className="chat-session-meta">
            <Text type="secondary">
              {dayjs(data.started_at).format('MMM D, YYYY h:mm A')}
            </Text>
            <Text type="secondary">
              {data.message_count} messages · {data.input_tokens + data.output_tokens} tokens · ${data.cost_usd}
            </Text>
            {data.model && <Text type="secondary">{data.model}</Text>}
          </div>
          <div className="chat-session-messages">
            {messages.map((msg) => {
              const match =
                msg.role === 'user' && sessionId
                  ? findGoldenPromptForUserMessage(msg, sessionId, goldenPrompts ?? [])
                  : undefined
              const pending =
                msg.role === 'user' &&
                isGoldenPromptPending(msg, match, createGolden, deleteGolden)
              return (
                <div key={msg.id}>
                  <MessageBubble message={msg} expandableToolCalls compactToolCalls={compactToolCalls} />
                  {msg.role === 'user' && adminUserId && sessionId && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <GoldenPromptControl
                        isMarked={!!match}
                        pending={pending}
                        onAdd={() =>
                          createGolden.mutate({
                            user_id: adminUserId,
                            prompt_text: getUserPromptText(msg),
                            prompt_type: 'normal',
                            session_ref: sessionId,
                          })
                        }
                        onRemove={() => match && deleteGolden.mutate(match.prompt_id)}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}
    </Modal>
  )
}

export default ChatSessionModal
